import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/lib/config';
import { useLang } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Info } from 'lucide-react';

function Sparkline({ points = [] }) {
  if (!points.length) return null;
  const w = 100;
  const h = 30;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(1, max - min);
  const stepX = w / (points.length - 1 || 1);
  const d = points
    .map((v, i) => {
      const x = i * stepX;
      const y = h - ((v - min) / range) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-8" aria-hidden="true">
      <path d={d} fill="none" stroke="#062B5C" strokeOpacity="0.7" strokeWidth="1.5" />
    </svg>
  );
}

function KPITile({ indicator }) {
  const { t } = useLang();
  const isPositive = indicator.trend === 'positive';
  return (
    <Card
      className="border-[#D7E1EF] bg-white"
      data-testid="dashboard-kpi-tile"
      data-key={indicator.key}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="text-sm font-medium text-[#062B5C]">{indicator.label}</div>
          <span
            data-testid="dashboard-kpi-delta"
            className={[
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
              isPositive ? 'bg-[#EAF6EF] text-[#138A4B]' : 'bg-[#FFF1E8] text-[#B54708]',
            ].join(' ')}
          >
            {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {indicator.delta}
          </span>
        </div>
        <div
          data-testid="dashboard-kpi-value"
          className="kpi-num mt-2 text-3xl font-semibold text-[#0B1220]"
        >
          {indicator.value}
        </div>

        <div className="mt-4">
          {indicator.unit === 'percent' || indicator.unit === 'index' ? (
            <Progress
              value={indicator.progress}
              className="h-2 bg-[#F5F8FC]"
              data-testid="dashboard-kpi-progress"
            />
          ) : (
            <Sparkline points={indicator.sparkline || []} />
          )}
        </div>

        <p className="mt-3 text-sm text-[#5B6B82] leading-relaxed">{indicator.explanation}</p>
        <div className="mt-3 flex items-center gap-2 text-xs text-[#5B6B82]">
          <Info className="h-3 w-3" />
          <span>
            {t('common.last_updated')}: {indicator.last_updated}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardPreview({ compact = false }) {
  const [data, setData] = useState({ indicators: [], leaderboard: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/dashboard/indicators`);
        if (!c) setData(res.data);
      } catch (e) {
        void e;
        if (!c) setError('Could not load dashboard');
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(compact ? 3 : 6)].map((_, i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    );
  }
  if (error) return <div className="text-sm text-[#B42318]">{error}</div>;

  const items = compact ? data.indicators.slice(0, 3) : data.indicators;
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      data-testid="dashboard-kpi-grid"
    >
      {items.map((i) => (
        <KPITile key={i.key} indicator={i} />
      ))}
    </div>
  );
}

export function DashboardLeaderboard() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/dashboard/indicators`);
        if (!c) setRows(res.data.leaderboard || []);
      } catch (e) {
        void e;
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => { c = true; };
  }, []);

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div data-testid="dashboard-department-leaderboard" className="overflow-hidden rounded-xl border border-[#D7E1EF] bg-white">
      <table className="w-full text-sm">
        <thead className="bg-[#F5F8FC] text-[#062B5C]">
          <tr>
            <th className="text-left font-medium px-4 py-3">{t('dashboard.col_rank')}</th>
            <th className="text-left font-medium px-4 py-3">{t('dashboard.col_dept')}</th>
            <th className="text-left font-medium px-4 py-3">{t('dashboard.col_score')}</th>
            <th className="text-left font-medium px-4 py-3">{t('dashboard.col_trend')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rank} className="border-t border-[#D7E1EF]">
              <td className="px-4 py-3 font-medium text-[#0B1220]">#{r.rank}</td>
              <td className="px-4 py-3 text-[#0B1220]">{r.department}</td>
              <td className="px-4 py-3 kpi-num text-[#0B1220]">{r.score}</td>
              <td className="px-4 py-3">
                <span
                  className={[
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                    r.trend === 'up' && 'bg-[#EAF6EF] text-[#138A4B]',
                    r.trend === 'down' && 'bg-[#FFF1E8] text-[#B54708]',
                    r.trend === 'flat' && 'bg-[#F5F8FC] text-[#5B6B82]',
                  ].filter(Boolean).join(' ')}
                >
                  {r.trend}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
